import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";

const MIN_NOTES = 10;

const backdropStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
  zIndex: 1000, display: "grid", placeItems: "center", padding: 20,
};
const dialogStyle = {
  background: "var(--bg-card)", border: "1px solid var(--bd)",
  borderRadius: "var(--r-md)", padding: 20, maxWidth: 560, width: "100%",
  maxHeight: "90vh", overflow: "auto",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
};

function ClaimQueue() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [openClaim, setOpenClaim] = useState(null);
  const [decision, setDecision] = useState("APPROVED");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState(null);
  const dialogRef = useFocusTrap({ active: !!openClaim, onClose: () => !submitting && setOpenClaim(null) });

  function fetchList() {
    setLoading(true);
    bdcApi
      .adListPendingClaims({ page, limit })
      .then((res) => {
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  function openDialog(row) {
    setOpenClaim(row);
    setDecision("APPROVED");
    setNotes("");
  }

  function closeDialog() {
    if (submitting) return;
    setOpenClaim(null);
    setNotes("");
  }

  async function submitDecision(ev) {
    ev.preventDefault();
    if (notes.trim().length < MIN_NOTES) {
      setFlash({ type: "danger", message: `Notes must be at least ${MIN_NOTES} characters.` });
      return;
    }
    setSubmitting(true);
    try {
      await bdcApi.adDecideClaim(openClaim.id, { decision, notes: notes.trim() });
      // Honest copy: API returns 200 on the DB write; the BDC notification is
      // queued for the cron, not delivered synchronously. Track it on the
      // Email log page (/ad/reports/email-log) if the BDC reports never
      // receiving the message.
      setFlash({ type: "info", message: `Claim ${openClaim.id} ${decision.toLowerCase()}. Notification queued for delivery.` });
      setOpenClaim(null);
      setNotes("");
      fetchList();
    } catch (err) {
      setFlash({
        type: "danger",
        message: err?.response?.data?.message || err.message || "Decide failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Manual referral claims"
        subtitle={<><span className="mono">{total}</span><span>pending · page {page} of {Math.max(totalPages, 1)}</span></>}
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Counterparty-confirmed claims awaiting admin decision. Approving writes to <code>attribution_ledger</code> (source = MANUAL).
      </p>

      {flash && <Alert tone={flash.type === "ok" ? "info" : flash.type}>{flash.message}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No claims awaiting admin review.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Claim #</th>
                  <th>Entity</th>
                  <th>Target</th>
                  <th>BDC</th>
                  <th>Counterparty confirmed</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">#{c.id}</td>
                    <td><Pill tone="blue">{c.entity_type}</Pill></td>
                    <td>
                      <div>{c.target_name || "—"}</div>
                      <div className="tbl-id">{c.target_email || "—"}</div>
                    </td>
                    <td className="mono">#{c.bdc_user_id}</td>
                    <td className="muted">{formatAuditTimestamp(c.counterparty_confirmed_at)}</td>
                    <td className="muted">{formatAuditTimestamp(c.createdAt)}</td>
                    <td className="r">
                      <Btn variant="primary" size="sm" onClick={() => openDialog(c)}>Review</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1 || submitting} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>{page} / {totalPages}</span>
          <Btn size="sm" disabled={page === totalPages || submitting} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}

      {openClaim && (
        <div
          style={backdropStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ad-claim-dialog-title"
          ref={dialogRef}
        >
          <form onSubmit={submitDecision} style={dialogStyle}>
            <h3 id="ad-claim-dialog-title" style={{ fontSize: "var(--t-xl)", fontWeight: 600, margin: "0 0 12px" }}>
              Review claim #{openClaim.id}
            </h3>
            <dl style={{ margin: "0 0 16px" }}>
              <KV label="Entity type" value={openClaim.entity_type} />
              <KV label="Target" value={`${openClaim.target_name || "—"}${openClaim.target_email ? ` <${openClaim.target_email}>` : ""}`} />
              <KV label="BDC" value={`#${openClaim.bdc_user_id}`} />
              <KV label="Supporting notes" value={openClaim.target_notes || "—"} />
            </dl>

            <fieldset style={{ border: 0, padding: 0, margin: "0 0 12px" }}>
              <legend className="field__label" style={{ marginBottom: 6 }}>Decision</legend>
              <div style={{ display: "flex", gap: 12 }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--t-md)" }}>
                  <input
                    type="radio"
                    name="decision"
                    value="APPROVED"
                    checked={decision === "APPROVED"}
                    onChange={() => setDecision("APPROVED")}
                    disabled={submitting}
                  />
                  Approve
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--t-md)" }}>
                  <input
                    type="radio"
                    name="decision"
                    value="REJECTED"
                    checked={decision === "REJECTED"}
                    onChange={() => setDecision("REJECTED")}
                    disabled={submitting}
                  />
                  Reject
                </label>
              </div>
            </fieldset>

            <div className="field">
              <label className="field__label" htmlFor="ad-claim-notes">
                Notes (required, min {MIN_NOTES} chars)
              </label>
              <textarea
                id="ad-claim-notes"
                className="field__textarea"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                autoFocus
                required
                minLength={MIN_NOTES}
              />
              <span className="field__hint">{notes.trim().length} / {MIN_NOTES}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Btn type="button" onClick={closeDialog} disabled={submitting}>Cancel</Btn>
              <Btn variant="primary" type="submit" disabled={submitting || notes.trim().length < MIN_NOTES}>
                {submitting ? "Submitting…" : `Confirm ${decision.toLowerCase()}`}
              </Btn>
            </div>
          </form>
        </div>
      )}
    </PageShell>
  );
}

const KV = ({ label, value }) => (
  <>
    <dt className="field__label" style={{ marginTop: 8 }}>{label}</dt>
    <dd style={{ margin: "2px 0 0", fontSize: "var(--t-md)" }}>{value}</dd>
  </>
);

export default ClaimQueue;
